import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import _ from "lodash";

// Material-UI components
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";

// Core components
import { SFGridContainer, SFGridItem } from "../grid";

// Custom components
import FullSearchDialogTitle from "./title";
import FullSearchDialogFilters from "./filters";
import FullSearchDialogResults from "./results";

// Other
import getStyled from "../../hooks/getStyled";
import getAdvancedQueryObject from "./filterGroups/getFilterModelObject";
import { FILTER_TYPES } from "./filterGroups/filterTypes";

const { styled } = getStyled();

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
  alignItems: "flex-start !important",
  backgroundColor: theme.palette.grey50.main,
  overflow: "hidden",
}));

const SFGridContainerStyled = styled(SFGridContainer)(({ theme }) => ({
  height: "100%",
}));

const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="left" ref={ref} {...props} />;
});

const SFFullSearchDialog = forwardRef(
  (
    {
      open,
      fullScreen,
      onClose = () => {},
      gridApi = {},
      title,
      filters = [],
      renderHeader,
      children,
      singleGetAllValuesUrl,
      keepFiltersOpen = false,
      menuItems = [],
      onMenuItemSelect,
      initiallySelectedMenuItem,
      externalFilters = [],
    },
    ref
  ) => {
    const searchFieldRef = useRef(null);

    // Controls the appearance of the filters section in the dialog
    const [filtersOpen, setFiltersOpen] = useState(keepFiltersOpen);

    // Tracks the value of the search field
    const [searchFieldValue, setSearchFieldValue] = useState("");

    const getInitialFilterSelection = () => {
      // Initialize the checkbox state with the default selected values for each filter group (if any)
      return Object.fromEntries(
        filters.map((filter) => [
          filter.colName,
          [...(filter.defaultSelectedValues ?? [])],
        ])
      );
    };

    // Object with the following structure:
    // `{ colName1: [val1, val2, ...], colName2: [val1, val2], ... }`
    // Tracks the selected filter checkboxes in the dialog
    const [filterSelection, setFilterSelection] = useState(
      getInitialFilterSelection()
    );

    const externalColNameTypes = Object.fromEntries(
      externalFilters.map((f) => [f.colName, f.type])
    );
    const externalFilterSelection = Object.fromEntries(
      externalFilters.map((f) => [f.colName, f.values])
    );

    const generateFilterModel = (
      searchFieldValue = "",
      filterSelection = {}
    ) => {
      const newFilterModel = _.cloneDeep(
        gridApi.getFilterModel ? gridApi.getFilterModel() : {}
      );

      // 1. Resolve the search text filter
      if (searchFieldValue.trim().length === 0) {
        // When the user clears the search field, delete the search text filter from the filter model
        delete newFilterModel.searchText;
      } else {
        // When the user types in the search field, update the search text filter in the filter model
        const searchTextFilterObj = {
          filter: JSON.stringify({
            value: searchFieldValue,
            excludedProperties: [],
          }),
          filterType: "text",
          type: "contains",
        };
        newFilterModel.searchText = searchTextFilterObj;
      }

      // 2. Resolve the advanced query filter
      const colNameTypes = Object.fromEntries(
        filters.map((filter) => [filter.colName, filter.type])
      );
      const advancedQueryObj = getAdvancedQueryObject(
        { ...filterSelection, ...externalFilterSelection },
        { ...colNameTypes, ...externalColNameTypes }
      );
      const advancedQueryFilterObj = {
        filter: JSON.stringify(advancedQueryObj),
        filterType: "textSearch",
        type: "contains",
      };
      newFilterModel.advancedQuery = advancedQueryFilterObj;

      return newFilterModel;
    };

    // Tracks the filter model object to update the data grid component
    const [filterModel, setFilterModel] = useState(
      generateFilterModel("", getInitialFilterSelection())
    );

    useEffect(() => {
      // Update the filter model of the data grid component with the new debounced filter model
      gridApi.setFilterModel && gridApi.setFilterModel(filterModel);    
    }, [JSON.stringify(filterModel), JSON.stringify(gridApi?.getFilterModel?.())]);

    useEffect(() => {
      // Effect to update the filter model state when the external filters change
      setFilterModel(generateFilterModel(searchFieldValue, filterSelection));
    }, [JSON.stringify(externalFilters)]);

    useImperativeHandle(ref, () => ({
      api: {
        // Function to clear the search field value and the filter selection
        reset: () => {
          searchFieldRef?.current?.api?.clear();
        },
      },
    }));

    // Function to update the filter selection state and the filter model state when a checkbox is clicked
    const updateFilterSelection = (colName, value) => {
      const newGroupSelection = new Set(filterSelection[colName]);
      if (newGroupSelection.has(value)) {
        newGroupSelection.delete(value);
      } else {
        newGroupSelection.add(value);
      }
      const newSelection = {
        ...filterSelection,
        [colName]: [...newGroupSelection],
      };
      setFilterSelection(newSelection);
      setFilterModel(generateFilterModel(searchFieldValue, newSelection));
    };

    // Function to reset the filter selection state and the advanced query filter when the user clears the checkboxes
    const resetFilterSelection = () => {
      const initialFilterSelection = getInitialFilterSelection();
      setFilterSelection(initialFilterSelection);
      setFilterModel(
        generateFilterModel(searchFieldValue, initialFilterSelection)
      );
    };

    // Function to update the search field value state and the search text filter when the user types in the search field
    const handleSearchFieldChange = (newValue) => {
      if (newValue.trim().length === 0) {
        // If the search field is empty, close the filters section
        !keepFiltersOpen && setFiltersOpen(false);
      } else {
        // If the search field is not empty, open the filters section
        setFiltersOpen(true);
      }

      setSearchFieldValue(newValue);
      // Every time the user types in the search field, clear the filter selection because we may have a new set of filter options
      const initialFilterSelection = getInitialFilterSelection();
      setFilterSelection(initialFilterSelection);
      setFilterModel(generateFilterModel(newValue, initialFilterSelection));
    };

    // Function to close the dialog and reset the search field value and filter selection
    const handleDialogClose = () => {
      onClose();
      !keepFiltersOpen && setFiltersOpen(false);
      setSearchFieldValue("");
      resetFilterSelection();
    };

    // Create a copy of the filterSelection object without the hidden filters
    const currFilterSelection = { ...filterSelection };
    filters.forEach((filter) => {
      if (filter.hide) {
        delete currFilterSelection[filter.colName];
      }
    });
    // Calculate the total number of selected checkboxes in the dialog (excluding hidden filters)
    const selectedCount = Object.values(currFilterSelection).reduce(
      (acc, curr) => acc + curr.length,
      0
    );

    return (
      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={handleDialogClose}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            overflow: "hidden",
            ...(fullScreen && {
              width: "100% !important",
            }),
          },
        }}
      >
        <FullSearchDialogTitle title={title} onClose={handleDialogClose} />
        <DialogContentStyled dividers>
          <SFGridContainerStyled
            spacing={0}
            direction="row"
            alignItems="stretch"
          >
            {filtersOpen && (
              <SFGridItem xs={3}>
                <FullSearchDialogFilters
                  filters={filters.filter((filter) => !filter.hide)}
                  currentSelection={filterSelection}
                  updateCurrentSelection={updateFilterSelection}
                  clearCurrentSelection={resetFilterSelection}
                  selectedCount={selectedCount}
                  singleGetAllValuesUrl={singleGetAllValuesUrl}
                  currentFilterModel={filterModel}
                  colNameTypes={Object.fromEntries(
                    filters.map((filter) => [filter.colName, filter.type])
                  )}
                  keepFiltersOpen={keepFiltersOpen}
                  externalColNameTypes={externalColNameTypes}
                  externalFilterSelection={externalFilterSelection}
                />
              </SFGridItem>
            )}
            <SFGridItem xs={filtersOpen ? 9 : 12}>
              <FullSearchDialogResults
                ref={searchFieldRef}
                onSearchFieldChange={handleSearchFieldChange}
                currentSearchText={searchFieldValue}
                currentSelection={filterSelection}
                renderHeader={renderHeader}
                menuItems={menuItems}
                onMenuItemSelect={onMenuItemSelect}
                initiallySelectedMenuItem={initiallySelectedMenuItem}
              >
                {children}
              </FullSearchDialogResults>
            </SFGridItem>
          </SFGridContainerStyled>
        </DialogContentStyled>
      </Dialog>
    );
  }
);

SFFullSearchDialog.propTypes = {
  /**
   * If `true`, the component is shown.
   */
  open: PropTypes.bool,
  /**
   * If `true`, the dialog is full-screen.
   */
  fullScreen: PropTypes.bool,
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: PropTypes.func,
  /**
   * The grid API object to perform search and filter operations on the data grid component.
   */
  gridApi: PropTypes.object.isRequired,
  /**
   * Text to display in the title area of the dialog.
   */
  title: PropTypes.string,
  /**
   * Array of menu items to render in the search dropdown menu.
   */
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  /**
   * Callback fired when a menu item from the dropdown is selected. Receives the selected value as an argument.
   */
  onMenuItemSelect: PropTypes.func,
  /**
   * The menu item that should be selected by default on dialog open.
   */
  initiallySelectedMenuItem: PropTypes.string,
  /**
   * Array of filter objects used to render groups of filter checkboxes in the dialog.
   */
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The name of the grid column that the filter group is associated with.
       */
      colName: PropTypes.string.isRequired,
      /**
       * The label to display for the filter group.
       */
      label: PropTypes.string.isRequired,
      /**
       * The type of filter group to render. `duration`, `rating`, `date` and `text` are currently supported. Default is `text`.
       */
      type: PropTypes.oneOf(Object.values(FILTER_TYPES)).isRequired,
      /**
       * The URL to fetch the distinct values for the filter group from. Only required for `text` type filters.
       */
      valuesUrl: PropTypes.string,
      /**
       * The filter model object to include in the API call when fetching the distinct values for the filter group.
       * Useful for fetching a subset of distinct values based on an external factor.
       */
      valuesUrlFilterModel: PropTypes.object,
      /**
       * If `true`, the filter group will have a search field to filter the available options. Only applicable for `text` type filters.
       */
      search: PropTypes.bool,
      /**
       * If `true`, the filter group will be hidden. Useful for filters that should be applied by default and not shown to the user.
       */
      hide: PropTypes.bool,
      /**
       * Function to hide a specific filter group option. Receives the value of the option as an argument. Should return a boolean.
       */
      hideOption: PropTypes.func,
      /**
       * Array of values that should always be selected by default, and their filters automatically applied in every search.
       * Cannot be cleared by the user.
       */
      defaultSelectedValues: PropTypes.arrayOf(PropTypes.string),
      /**
       * Function to format the labels of the filter group options. Receives the value of the option as an argument.
       * Should return a string or React component.
       */
      labelFormatter: PropTypes.func,
      /**
       * Function to sort the filter group options. Receives two values as arguments.
       * Example usage:
       * ```jsx
       * comparator: (valueA, valueB) => valueA.localeCompare(valueB),
       * ```
       */
      comparator: PropTypes.func,
    })
  ),
  /**
   *  Function to render a custom component before the search results.
   *  Receives the current search value and filter selection as arguments.
   *  Should return a React component.
   *  Example usage:
   *  ```jsx
   *  renderHeader={(currentSearchText, currentSelection) => (
   *    <SFStack direction="row" alignItems="center" justifyContent="space-between">
   *      <SFTypography variant="body2bold">{`Results for "${currentSearchText}"`}</SFTypography>
   *      <SFTypography variant="body2bold">{`Filters: ${Object.values(currentSelection).reduce((acc, curr) => acc + curr.length, 0)}`}</SFTypography>
   *    </SFStack>
   *  )}
   *  ```
   */
  renderHeader: PropTypes.func,
  /**
   * If `true`, the filters section will remain open when the search field is empty.
   */
  keepFiltersOpen: PropTypes.bool,
};

export default SFFullSearchDialog;
